import type { Canvas, Point } from "fabric";
import { CameraObject } from "./CameraObject";

/**
 * 클릭 지점에 판정이 걸리는 카메라 마커들.
 *
 * fabric 이 대상을 고를 때 쓰는 것과 같은 기준(`_checkTarget`)을 그대로 쓴다.
 * perPixelTargetFind 까지 포함되므로 "보이는 그림"에 걸린 마커만 후보가 된다.
 */
export function getCameraCandidatesAt(canvas: Canvas, point: Point): CameraObject[] {
  const checkTarget = (
    canvas as unknown as { _checkTarget?: (o: unknown, p: Point) => boolean }
  )._checkTarget;

  return canvas.getObjects().filter((obj): obj is CameraObject => {
    if (!(obj instanceof CameraObject)) return false;
    if (!obj.evented || !obj.visible) return false;
    return checkTarget
      ? checkTarget.call(canvas, obj, point)
      : obj.containsPoint(point);
  });
}

/** 클릭 지점에서 카메라 위치(dot)까지 거리의 제곱 */
function distanceSquared(camera: CameraObject, point: Point): number {
  const dx = (camera.left ?? 0) - point.x;
  const dy = (camera.top ?? 0) - point.y;
  return dx * dx + dy * dy;
}

/**
 * 겹친 카메라 중 이번 클릭에서 고를 카메라.
 *
 * - 후보가 하나면 그대로.
 * - 여럿이면 카메라 위치(dot)가 클릭 지점에 가까운 순으로 세운다. 겨냥한 것이 잡힐
 *   확률이 가장 높고, 순서가 항상 같아 예측 가능하다.
 * - 그중 지금 선택된 카메라가 있으면 **그 다음 것**을 돌려준다. 같은 자리를 반복해서
 *   누르면 아래 깔린 카메라까지 차례로 닿는다 (한 바퀴 돌면 처음으로).
 *
 * 부채꼴은 투명도 20% 로 칠해진 면이라 이웃 카메라끼리 자주 겹친다. 픽셀 판정만으로는
 * 그 지점에서 둘 다 "보이는 그림"이라 구분되지 않으므로 이 순환이 필요하다.
 */
export function pickCameraAt(
  canvas: Canvas,
  point: Point,
  currentCameraId: number | null,
): CameraObject | null {
  const candidates = getCameraCandidatesAt(canvas, point);
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];

  const sorted = [...candidates].sort(
    (a, b) => distanceSquared(a, point) - distanceSquared(b, point),
  );
  const currentIndex = sorted.findIndex((c) => c.cameraId === currentCameraId);
  if (currentIndex === -1) return sorted[0];
  return sorted[(currentIndex + 1) % sorted.length];
}
